import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import {
  IScheduleList,
  ICancelScheduleAppointments,
  IAppointments,
} from "@/models/ApiRequestResponse/doctor";
import {
  IScheduleAppointmentDateRangeParams,
  ISelectedDate,
} from "@/models/doctor";

import {
  getScheduleAppointmentListingApi,
  searchAppointmentsApi,
} from "@/utils/apis/doctor/appointment";

import { RootState } from "@/store/store";

const initialStateScheduleSelection = {
  isSelectionOn: false as boolean,
  scheduleSelection: {} as { [key: string]: boolean },
  appointmentSelection: {} as { [key: string]: boolean },
  selectedSchedules: [] as ICancelScheduleAppointments[],
  selectedAppointments: [] as string[],
};

export const fetchScheduleAppointmentList = createAsyncThunk(
  "doctor/schedule",
  async ({ start, end }: IScheduleAppointmentDateRangeParams) => {
    const response = await getScheduleAppointmentListingApi({ start, end });
    return response;
  }
);

export const fetchSeachedAppointmentList = createAsyncThunk(
  "doctor/search-schedule",
  async ({
    search,
    startDate,
    endDate,
  }: {
    search: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await searchAppointmentsApi(search, startDate, endDate);
    return response;
  }
);

const initialState = {
  selection: initialStateScheduleSelection,
  scheduleAppointmentList: [] as IScheduleList[],
  filteredScheduleAppointmentList: [] as IScheduleList[],
  searchAppointmentsList: [] as IAppointments[],
  selectedDate: null as ISelectedDate | null,
  isLoading: true as boolean,
  isSearchLoading: false as boolean,
  isScheduleColumnShown: true as boolean,
  isCancelSchedule: false as boolean,
};

const ScheduleSlice = createSlice({
  name: "schedule",
  initialState,
  reducers: {
    resetScheduleAppointment: () => initialState,
    resetScheduleSelection: (state) => {
      state.selection = initialStateScheduleSelection;
    },
    setIsSelectionOn: (state) => {
      state.selection.isSelectionOn = !state.selection.isSelectionOn;
    },
    setIsScheduleColumnShown: (state) => {
      state.isScheduleColumnShown = !state.isScheduleColumnShown;
    },
    setScheduleAppointmentList: (state, action) => {
      state.scheduleAppointmentList = action.payload.filter(
        (schedule: IScheduleList) =>
          schedule.schedules.some((s) => s.appointments?.length > 0)
      );
    },
    setFilteredScheduleAppointmentList: (state, action) => {
      state.filteredScheduleAppointmentList = action.payload;
    },
    setScheduleSelection: (state, action) => {
      state.selection.scheduleSelection = {
        ...state.selection.scheduleSelection,
        ...action.payload,
      };
      const checkedSchedules = [];
      for (let scheduleDetailKey in state.selection.scheduleSelection) {
        if (state.selection.scheduleSelection[scheduleDetailKey]) {
          const [scheduleId, date] = scheduleDetailKey.split("&");
          checkedSchedules.push({
            date,
            scheduleIds: [scheduleId],
          });
        }
      }
      state.selection.selectedSchedules = checkedSchedules;
    },
    setAppointmentSelection: (state, action) => {
      state.selection.appointmentSelection = {
        ...state.selection.appointmentSelection,
        ...action.payload,
      };
      const checkedAppointmentsIds = [];
      for (let patientId in state.selection.appointmentSelection) {
        if (state.selection.appointmentSelection[patientId]) {
          checkedAppointmentsIds.push(patientId);
        }
      }
      state.selection.selectedAppointments = checkedAppointmentsIds;
    },
    resetSearchAppointments: (state) => {
      state.searchAppointmentsList = [];
    },

    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setIsCancelSchedule: (state, action) => {
      state.isCancelSchedule = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // schedule appointments list
      .addCase(fetchScheduleAppointmentList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchScheduleAppointmentList.fulfilled, (state, action) => {
        state.isLoading = false;
        if ("result" in action.payload) {
          state.scheduleAppointmentList = action.payload.result;
        }
      })
      .addCase(fetchScheduleAppointmentList.rejected, (state) => {
        state.isLoading = false;
      })
      // searched appointments list
      .addCase(fetchSeachedAppointmentList.pending, (state) => {
        state.isSearchLoading = true;
      })
      .addCase(fetchSeachedAppointmentList.fulfilled, (state, action) => {
        state.isSearchLoading = false;
        if ("result" in action.payload) {
          state.searchAppointmentsList = action.payload.result;
        }
      })
      .addCase(fetchSeachedAppointmentList.rejected, (state) => {
        state.isSearchLoading = false;
      });
  },
});

const selectSchedule = (state: RootState) => state.schedule;

export const schedule = createSelector([selectSchedule], (selectSchedule) => {
  return {
    isCancelSchedule: selectSchedule.isCancelSchedule,
    scheduleSelection: selectSchedule.selection,
    scheduleAppointmentList: selectSchedule.scheduleAppointmentList,
    filteredScheduleAppointmentList:
      selectSchedule.filteredScheduleAppointmentList,
    searchAppointmentsList: selectSchedule.searchAppointmentsList,
    selectedDate: selectSchedule.selectedDate,
    isLoading: selectSchedule.isLoading,
    isSearchLoading: selectSchedule.isSearchLoading,
    isScheduleColumnShown: selectSchedule.isScheduleColumnShown,
  };
});

export const {
  resetScheduleAppointment,
  resetSearchAppointments,
  resetScheduleSelection,
  setIsSelectionOn,
  setScheduleSelection,
  setAppointmentSelection,
  setSelectedDate,
  setScheduleAppointmentList,
  setFilteredScheduleAppointmentList,
  setIsScheduleColumnShown,
  setIsCancelSchedule,
} = ScheduleSlice.actions;
export default ScheduleSlice.reducer;
